import type { ButtonProps } from '@magicschool/spellbook/components/Button';
import { Button } from '@magicschool/spellbook/components/Button';
import type { CalendarProps } from '@magicschool/spellbook/components/Calendar';
import { Calendar } from '@magicschool/spellbook/components/Calendar';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { cn } from '@magicschool/spellbook/lib/utils';
import { forwardRef, useMemo } from 'react';

export type FilterChipDateTriggerProps = ButtonProps & {
  selectedDate: Date;
  locale: string;
  'data-testid'?: string;
};

export const FilterChipDateTriggerSlot = Slot<FilterChipDateTriggerProps>({
  displayName: 'FilterChipDate.Trigger',
  renderDefault: (props) => {
    const FilterChipDateTrigger: React.FC<FilterChipDateTriggerProps> = useMemo(
      () =>
        forwardRef(({ children, className, selectedDate, locale, onClick, 'data-testid': dataTestId, ...props }, ref) => {
          const displayText = useMemo(() => {
            return selectedDate ? selectedDate.toLocaleDateString(locale) : children || 'Date';
          }, [selectedDate, locale, children]);

          const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            onClick?.(e);
          };

          return (
            <Button
              ref={ref}
              data-testid={dataTestId}
              onClick={handleOnClick}
              data-spellbook-name="filter-chip-date-trigger"
              color="neutral"
              variant="outlined"
              aria-pressed={Boolean(selectedDate)}
              className={cn(
                [
                  /**
                   * @TODO [SPE-16] Centralize “Selected” Color Tokens into Shared Style Utilities
                   * https://linear.app/magicschool/issue/SPE-16/centralize-selected-color-tokens-into-shared-style-utilities
                   */
                  'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:border-disabled-background',
                  'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:bg-disabled-background',
                  'aria-pressed:[&[aria-disabled="true"]:not([aria-busy="true"])]:text-disabled-foreground',
                  'aria-pressed:bg-selected-default-background',
                  'aria-pressed:hover:bg-selected-default-background-hover',
                  'aria-pressed:active:bg-selected-default-background-active',
                  'aria-pressed:text-selected-default-foreground',
                  'aria-pressed:hover:text-selected-default-foreground-hover',
                  'aria-pressed:active:text-selected-default-foreground-active',
                  'aria-pressed:[&_svg]:text-selected-default-icon',
                  'aria-pressed:hover:[&_svg]:text-selected-default-icon-hover',
                  'aria-pressed:active:[&_svg]:text-selected-default-icon-active',
                  'aria-pressed:border-selected-default-stroke',
                  'aria-pressed:hover:border-selected-default-stroke-hover',
                  'aria-pressed:active:border-selected-default-stroke-active',
                  'aria-pressed:data-[state=open]:border-selected-default-stroke-active',
                  'aria-pressed:data-[state=open]:bg-selected-default-background-hover',
                ],
                className,
              )}
              {...props}
            >
              <Button.LeadingIcon name="calendar-days" />
              {displayText}
              <Button.ToggleIcon />
            </Button>
          );
        }),
      [],
    );

    return <FilterChipDateTrigger {...props} />;
  },
});

export const FilterChipCalendarSlot = Slot<CalendarProps>({
  displayName: 'FilterChipDate.Calendar',
  preventOverride: true,
  isRenderedByDefault: true,
  renderDefault: ({ ...props }) => {
    return <Calendar data-spellbook-name="filter-chip-date-calendar" {...props} autoFocus />;
  },
});
